import type { WidgetEvent } from '../types';
import type { ApiClient } from './api';
import type { State } from './state';
import { readJson, writeJson } from './util';

const QUEUE_KEY = 'fetrace:queue';
const MAX_STORED = 50;

/**
 * Batches events (5 s / 20 events), flushes with sendBeacon on unload, persists failed
 * batches to localStorage and retries with exponential backoff (PLAN.md §2.2 item 9).
 */
export class Transport {
  private pending: WidgetEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private backoff = 2000;
  private flushing = false;
  batchSize = 20;
  intervalMs = 5000;

  constructor(
    private readonly state: State,
    private readonly api: ApiClient,
  ) {}

  start(): void {
    const stored = readJson<WidgetEvent[]>(this.state.local, QUEUE_KEY, []);
    if (stored.length) {
      this.state.local?.removeItem(QUEUE_KEY);
      this.pending.push(...stored);
      this.schedule(1000);
    }
    window.addEventListener('pagehide', () => this.flushBeacon());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flushBeacon();
    });
    window.addEventListener('online', () => this.schedule(500));
  }

  enqueue(event: WidgetEvent): void {
    this.pending.push(event);
    if (this.pending.length >= this.batchSize) void this.flush();
    else this.schedule(this.intervalMs);
  }

  size(): number {
    return this.pending.length;
  }

  private schedule(ms: number): void {
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, ms);
  }

  async flush(): Promise<void> {
    if (this.flushing || this.pending.length === 0) return;
    if (!navigator.onLine) {
      this.persist();
      return;
    }
    this.flushing = true;
    const batch = this.pending.splice(0, this.batchSize);
    try {
      const res = await this.api.events(batch);
      this.backoff = 2000;
      this.state.log('sent', res);
      if (this.pending.length) this.schedule(this.intervalMs);
    } catch (err) {
      const status = (err as { status?: number }).status ?? 0;
      if (status >= 400 && status < 500 && status !== 429) {
        // Rejected for good (bad key, origin, validation) — drop, never retry.
        this.state.log('dropped batch', status, err);
      } else {
        this.pending.unshift(...batch);
        this.persist();
        this.retry();
      }
    } finally {
      this.flushing = false;
    }
  }

  private retry(): void {
    if (this.retryTimer) return;
    const delay = this.backoff;
    this.backoff = Math.min(this.backoff * 2, 60_000);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.flush();
    }, delay);
  }

  private persist(): void {
    writeJson(this.state.local, QUEUE_KEY, this.pending.slice(-MAX_STORED));
  }

  private flushBeacon(): void {
    if (this.pending.length === 0) return;
    const batch = this.pending.splice(0, this.batchSize);
    if (!this.api.beacon(batch)) {
      this.pending.unshift(...batch);
    }
    this.persist();
  }
}
